const SESSION_KEY = 'kablewy_session';
const SESSION_EXPIRY = 'kablewy_session_expiry';
const VALID_ROLES = ['owner', 'admin', 'member', 'guest'];

const normalizeRole = (role, fallback) => {
  const defaultRole = typeof fallback === 'string' ? fallback : undefined;
  if (!role && !defaultRole) {
    return undefined;
  }
  const normalized = typeof role === 'string' ? role.trim().toLowerCase() : '';
  if (normalized && VALID_ROLES.includes(normalized)) {
    return normalized;
  }
  return defaultRole && VALID_ROLES.includes(defaultRole.trim().toLowerCase())
    ? defaultRole.trim().toLowerCase()
    : undefined;
};

export const tokenProvider = {
  setToken(token, expiresAt) {
    // Update existing session data with new token
    const data = this.getSessionData();
    if (data) {
      data.sessionToken = token;
      data.expiresAt = expiresAt || (Date.now() + 24 * 60 * 60 * 1000); // Default 24 hours
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(data));
      sessionStorage.setItem(SESSION_EXPIRY, data.expiresAt);
    } else {
      console.warn('[DEBUG] Token provider setToken - no existing session data to update');
    }
  },

  // Add refresh token functionality
  async refreshToken() {
    const currentToken = this.getToken();
    if (!currentToken) return null;
    
    try {
      // Call API refresh endpoint
      const response = await fetch('/v1/auth/refresh', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${currentToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) throw new Error('Token refresh failed');
      
      const data = await response.json();
      this.setToken(data.session_token, data.expires_at);
      return data.session_token;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return null;
    }
  },

  setSessionData(data) {
    if (data) {
      if (data.expiresAt) {
        sessionStorage.setItem(SESSION_EXPIRY, data.expiresAt);
      }
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(data));
    } else {
      sessionStorage.removeItem(SESSION_KEY);
      sessionStorage.removeItem(SESSION_EXPIRY);
    }
  },

  getSessionData() {
    const data = sessionStorage.getItem(SESSION_KEY);
  
    if (!data) return null;
    try {
      const parsedData = JSON.parse(data);
     
      return parsedData;
    } catch (error) {
      console.error('Error parsing session data:', error);
      return null;
    }
  },

  getToken() {
    const data = this.getSessionData();
    return data?.sessionToken;
  },

  getTokenExpiry() {
    return sessionStorage.getItem(SESSION_EXPIRY);
  },

  isTokenExpired() {
    // First try to get expiry from session data (more reliable)
    const sessionData = this.getSessionData();
    if (sessionData && sessionData.expiresAt) {
      try {
        const expiryTimestamp = parseInt(sessionData.expiresAt);
        if (!isNaN(expiryTimestamp)) {
          return expiryTimestamp < Date.now();
        }
      } catch (e) {
        console.error("Error parsing session expiresAt:", e);
      }
    }
    
    // Fallback to separate expiry key
    const expiry = this.getTokenExpiry();
    if (!expiry) return true;
    
    return parseInt(expiry) < Date.now();
  },

  isTokenExpiringSoon(minutes = 5) {
    // First try to get expiry from session data (more reliable)
    const sessionData = this.getSessionData();
    if (sessionData && sessionData.expiresAt) {
      try {
        const expiryTimestamp = parseInt(sessionData.expiresAt);
        if (!isNaN(expiryTimestamp)) {
          return expiryTimestamp - Date.now() < minutes * 60 * 1000;
        }
      } catch (e) {
        console.error("Error parsing session expiresAt for soon check:", e);
      }
    }
    
    // Fallback to separate expiry key
    const expiry = this.getTokenExpiry();
    if (!expiry) return true;
    
    return parseInt(expiry) - Date.now() < minutes * 60 * 1000;
  },

  clearToken() {
    const data = this.getSessionData();
    if (data) {
      data.sessionToken = null;
      data.expiresAt = null;
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(data));
      sessionStorage.removeItem(SESSION_EXPIRY);
    }
  },

  clear() {
    sessionStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(SESSION_EXPIRY);
  },

  updateFromValidation(validationData) {
    if (!validationData) {
      return false;
    }
    
    // Make sure we have the required fields
    if (!validationData.sessionToken || !validationData.orgId) {
      return false;
    }
    
    // If we have user data from validation, merge it with existing session
    if (validationData.userData && Object.keys(validationData.userData).length > 0) {
      const existing = this.getSessionData() || {};
      const existingUser = existing.userData || {};
      const validatedUser = validationData.userData || {};

      // Prefer locally-updated permissions if present; otherwise use server
      const mergedPermissions = (existingUser.permissions && Object.keys(existingUser.permissions || {}).length)
        ? existingUser.permissions
        : validatedUser.permissions;

      const mergedPermissionsJson = (() => {
        try {
          if (mergedPermissions) return JSON.stringify(mergedPermissions);
        } catch (_) {}
        return validatedUser.permissions_json || existingUser.permissions_json;
      })();

      const resolvedRole = normalizeRole(validatedUser.role, existingUser.role);

      const updatedSession = {
        sessionToken: validationData.sessionToken,
        email: validationData.email,
        orgId: validationData.orgId,
        lastActive: new Date().toISOString(),
        expiresAt: validationData.expiresAt || (Date.now() + 24 * 60 * 60 * 1000),
        userData: {
          ...existingUser,
          ...validatedUser,
          // Ensure permissions do not regress if locally configured
          permissions: mergedPermissions || validatedUser.permissions,
          permissions_json: mergedPermissionsJson
        }
      };

      if (resolvedRole) {
        updatedSession.userData.role = resolvedRole;
      } else if (existingUser.role) {
        const normalizedExisting = normalizeRole(existingUser.role);
        if (normalizedExisting) {
          updatedSession.userData.role = normalizedExisting;
        } else {
          delete updatedSession.userData.role;
        }
      } else {
        delete updatedSession.userData.role;
      }

      sessionStorage.setItem(SESSION_KEY, JSON.stringify(updatedSession));
      sessionStorage.setItem(SESSION_EXPIRY, updatedSession.expiresAt);
      return true;
    }
    
    // If no userData in validation response, just update the token
    this.setToken(validationData.sessionToken, validationData.expiresAt);
    return true;
  }
}; 
